import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailX, XCircle } from "lucide-react";
import { api } from "../lib/api";


export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking");
  const [emailHint, setEmailHint] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    api.get("/email/unsubscribe/status", { params: { token } })
      .then(({ data }) => {
        setEmailHint(data.email_hint || "");
        setStatus(data.subscribed ? "ready" : "done");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const unsubscribe = async () => {
    setSubmitting(true);
    try {
      await api.post("/email/unsubscribe", null, { params: { token } });
      setStatus("done");
    } catch {
      setStatus("invalid");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="unsubscribe-page" className="min-h-screen px-6 pb-20 pt-32">
      <div className="mx-auto max-w-lg rounded-2xl border border-white/10 bg-[#0a0a0c] p-8 sm:p-10">
        {status === "checking" && (
          <div className="py-10 text-center text-zinc-400">
            <Loader2 className="mx-auto mb-4 h-7 w-7 animate-spin text-[#e11d48]" />
            Checking unsubscribe link…
          </div>
        )}

        {status === "ready" && (
          <>
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-[#e11d48]/15">
              <MailX className="h-5 w-5 text-[#fb7185]" />
            </div>
            <h1 className="font-[Outfit] text-3xl font-bold">Stop receiving promotions?</h1>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Promotional emails for <span className="font-medium text-zinc-200">{emailHint}</span> will be stopped.
              Important notifications about your account, payments, products, and licenses may still be sent.
            </p>
            <button
              type="button"
              data-testid="unsubscribe-confirm"
              onClick={unsubscribe}
              disabled={submitting}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#e11d48] px-5 py-3 font-semibold transition hover:bg-[#be123c] disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Processing…" : "Unsubscribe"}
            </button>
            <Link to="/" className="mt-4 block text-center text-sm text-zinc-500 hover:text-white">Cancel and Go Back</Link>
          </>
        )}

        {status === "done" && (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h1 className="mt-5 font-[Outfit] text-3xl font-bold">Promotional Emails Stopped</h1>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              {emailHint ? `${emailHint} will no longer receive marketing emails.` : "Your marketing preference has been updated."}
              You may still receive important notifications and can subscribe again from your customer dashboard.
            </p>
            <Link to="/" className="mt-8 inline-flex rounded-full border border-white/10 px-5 py-2.5 text-sm font-semibold hover:bg-white/5">Back to Website</Link>
          </div>
        )}

        {status === "invalid" && (
          <div className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-red-400" />
            <h1 className="mt-5 font-[Outfit] text-3xl font-bold">Invalid Link</h1>
            <p className="mt-3 text-sm text-zinc-400">The unsubscribe link is invalid or the account email address has changed.</p>
            <Link to="/login" className="mt-8 inline-flex rounded-full bg-[#e11d48] px-5 py-2.5 text-sm font-semibold">Open Dashboard</Link>
          </div>
        )}
      </div>
    </div>
  );
}
